import React from 'react'
import ReactDayPickerInput from 'react-day-picker/DayPickerInput';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import Multiselect, { Option } from '../../components/Multiselect';
import Navigation from '../../components/navigation/Navigation';
import ErrorMessage from '../../components/visuals/ErrorMessage';
import General from '../../lib/dataAccess/General';
import { months_long_se, months_short_se, weekdays_long_se, weekdays_short_se } from '../../lib/constants';
import { Forecast } from '../../lib/dataAccess/DataAccess';
import { Prognosis } from '../../lib/utilities';
import {  Alert, Col, FormLabel, Row, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import Accordion from '../../components/Accordion';
import Table from '../../components/Table';
import { ColumnDescription } from 'react-bootstrap-table-next';
import Loading from '../../components/visuals/Loading';

interface Props {
  role: string
}

interface State {
  error: string | null,
  isLoading: boolean,

  startDate: Date,
  endDate: Date,
  showDropdown: boolean;
  selectedStudent?: Option;
  students: Option[];
  graphData:any;
  listData:any[]
  courses: any[];
  courseFilter:Option[];
  graphView:number;
}

export default class HistoryPage extends React.Component<Props, State> {
  constructor(props: Props) {
      super(props);
      let today = new Date();
      let startDate = today.getMonth() > 6 ? new Date(`${today.getFullYear()}-08-01`) : new Date(`${today.getFullYear()-1}-08-01`)
      let endDate = today.getMonth() > 6 ? new Date(`${today.getFullYear()+1}-08-01`) : new Date(`${today.getFullYear()}-08-01`)
      this.state = {
        error : null,
        isLoading : false,
        startDate : startDate,
        endDate : endDate,
        showDropdown: true,
        selectedStudent: undefined,
        students: [],
        graphData:[],
        listData:[],
        courses: [],
        courseFilter: [],
        graphView: 1
      }
  }

  async componentDidMount(){
    const students = (await General.getStudents())
      .sort((a, b) => a.lastName > b.lastName ? 1 : -1)
      //@ts-ignore
      .map(student => ({ label: `${student.firstName} ${student.lastName}, ${student.className}, ${student.schoolUnitCode}`, value: student.id }))
    //this.colors = this.colors.sort(() => Math.random() - 0.5);
    this.setState({
      students
    });
  }

  private gradeToPrognosis = {
    'A':4,
    'B':4,
    'C':4,
    'D':3,
    'E':2,
    'F':1
  };

  private prognosisOptions = Prognosis.options(true);

  private colors = [
    "#2E0014",
"#A5907E",
"#C5D86D",
"#FFC43D",
"#FAA916",
"#A9AFD1",
"#ff7096",
"#EE6C4D",
"#BA181B",
"#602706",
"#593959",
"#84A65C",
"#ADB1A9",
"#ff0a54",
"#ff477e",
"#FB6107",
"#D16666",
"#DAD2D8",
"#D0CFEC",
"#F16055",
"#ED2A1D",
"#0088FE",
"#CE6D8B",
"#C2476E",
"#69735f",
"#A9AFD1",
"#A5907E",
"#F16055",
    "#00ff00",
    "#0099ff",
    "#ff0000",
    "#ffff00",
    "#00ffff",
    "#ff00ff",
    "#ff6600",
    "#009933",
    "#0099cc",
    "#0000cc",
    "#9966ff",
    "#cc6699",
    "#993333",
    "#ff5050",
    "#999966",
    "#003300",
    "#003366",
    "#000066",
    "#660066"
  ]

  private getGraphData = (studentForecasts : any[], courses : any[]) => {
    const timeStamps = studentForecasts
        .map(studentForecast => new Date(studentForecast.sysStartTime).setHours(0,0,0,0).valueOf())
        .filter((time, i, self) => self.indexOf(time) == i);

      let data = timeStamps.map(time => {
        return studentForecasts
          .filter(forecast => new Date(forecast.sysStartTime).setHours(0,0,0,0).valueOf() == time)
          .reduce((prev:any, curr:any) => {
            if(curr.prognosis != 5 && curr.prognosis != null)
              prev[curr.forecast.course.displayName] = curr.prognosis;
            if(curr.grade != '' && curr.grade != '-'){
              const grade : 'A'|'B'|'C'|'D'|'E'|'F' = curr.grade;
              prev[curr.forecast.course.displayName] = this.gradeToPrognosis[grade];
              prev[`${curr.forecast.course.displayName}grade`] = grade;
            }
            prev[`${curr.forecast.course.displayName}schoolYear`] = curr.schoolYear;
            prev[`${curr.forecast.course.displayName}courseEnd`] = curr.forecast.course.courseInstance.endDate;
            return prev;
          }, {time});
      })
      .sort((a, b) => a.time - b.time);

      courses.forEach(courseName => {
        const lastPost = data.filter((point:any) => point[courseName]).sort((a, b) => b.time - a.time)[0];
        
        if(!lastPost[`${courseName}grade`]){
          const lastTimestamp = Math.min(new Date().setHours(0,0,0,0).valueOf(), new Date(lastPost[`${courseName}courseEnd`]).valueOf());
          if(!data.find(point => point.time == lastTimestamp)){
            data.push({time: lastTimestamp, f: new Date(lastTimestamp).toLocaleDateString()});
          }
          const extendedData = data.find(point => point.time == lastTimestamp);
          extendedData[`${courseName}grade`] = lastPost[`${courseName}grade`];
          extendedData[courseName] = lastPost[courseName];
          extendedData[`${courseName}schoolYear`] = lastPost[`${courseName}schoolYear`];
        }
      })
      data = data.sort((a, b) => a.time - b.time)

      return data;
  }

  private getListData = (studentForecasts : any[]) => {
    return studentForecasts
    .map(data => (
    {...data, time: new Date(data.sysStartTime).valueOf(),
      //@ts-ignore
      prognosis: (data.grade != '' && data.grade != '-' ? this.gradeToPrognosis[data.grade] : data.prognosis),
      key: `${data.sysStartTime}-${data.courseId}`
    }))
    .filter(data => data.prognosis != 5 && data.prognosis)
  }

  private onChangeStudent = async (selected : any) => {
    if(selected.length > 0) {
      const selectedStudent = selected[selected.length - 1]
      this.prognosisOptions = Prognosis.options(selectedStudent.programName == "GR")
      this.setState(
          {
              selectedStudent: selectedStudent, 
              showDropdown: false,
              isLoading: true
          }
      );
      let studentForecasts : any[] = [];
      try {
        studentForecasts = await Forecast.getStudentHistory(selectedStudent.value);
        const courses = studentForecasts.reduce((prev:any[], curr : any) => {
          if(prev.indexOf(curr.forecast.course.displayName) < 0){
            prev.push(curr.forecast.course.displayName);
          }
          return prev;
        }, []).sort();
        
        const graphData = this.getGraphData(studentForecasts, courses);
        const listData = this.getListData(studentForecasts);
        this.setState({
          graphData: graphData,
          courses: courses,
          showDropdown: true,
          error: null,
          isLoading: false,
          listData: listData
        });
      } catch (error) {
        this.setState({error: error as string});
      }
      
      
    }
  }

  private getTicks = (startDate:Date, endDate:Date, gap: number) : number[] => {
    let ticks = [];
    let date = new Date(startDate.toLocaleDateString());
    while(date.valueOf() <= endDate.valueOf()){
      ticks.push(date.valueOf());
      date.setMonth(date.getMonth()+gap);
    }
    if(ticks.length > 25 && gap != 3){
      return this.getTicks(startDate, endDate, 3);
    }
    if(ticks.length > 15 && gap != 3){
      return this.getTicks(startDate, endDate, 2);
    }
    return ticks;
  }

  private columns : ColumnDescription<any, any>[] = [{
    dataField: 'id',
    text: 'ID',
    hidden: true
    }, {
    dataField: 'sysStartTime',
    text: 'Datum',
    headerStyle: {width:'7rem'},
    formatter: (cell, row: any) =>  (<div>
        {new Date(cell).toLocaleDateString()}
    </div>)
    }, {
    dataField: 'user',
    text: 'Redigerad av',
    formatter: (cell, row: any) =>  (<div>
      {cell.userName}
  </div>)
    }, {
    dataField: 'prognosis',
    text: 'Prognos',
    style: {padding: "0px"},
    formatter: (cell: number, row: any) => (<div style={{background: this.prognosisOptions[cell-1].color, padding:"0.75rem", color: this.prognosisOptions[cell-1].textColor}}>
        {this.prognosisOptions[cell-1].value}
    </div>)
  },
  {
    dataField: 'grade',
    text: 'Betyg',
    headerStyle: {width:'7rem'}
  },
  {
    dataField: 'schoolYear',
    text: 'Årskurs',
    headerStyle: {width:'7rem'}
  }
];

  render(): React.ReactNode {
      let lines = this.state.courses.map((courseName,i) => {
        return <Line key={courseName} strokeWidth={5} type="monotone" connectNulls dataKey={courseName} stroke={this.colors[i]}   />
      });
      if(this.state.courseFilter.length > 0){
        lines = lines.filter(courseName => this.state.courseFilter.find(course => course.value == courseName.key));
      }
      return <div className="render-graduation-prognosis">
      <Navigation role={this.props.role}/>
      <div className="junit-container container-fit" style={{overflow:'auto'}}>
                    <ErrorMessage hidden={this.state.error === null} style={{marginBottom:'1rem'}}>{this.state.error}</ErrorMessage>
                    <br/>
                        <h4>Elev</h4>
                        {
                          this.state.showDropdown &&
                          <Row>
                          <Col md={10} style={{marginBottom: 10}}>
                            <Multiselect 
                              value={this.state.selectedStudent ? [this.state.selectedStudent] : []}
                              onChange={this.onChangeStudent}
                              options={this.state.students}
                              ItemRenderer={({
                                  checked,
                                  option,
                                  onClick,
                                  disabled,
                              }: {
                                  checked: boolean,
                                  option: {
                                      value: any;
                                      label: string;
                                      key?: string;
                                      disabled?: boolean;
                                      header?: boolean;
                                  },
                                  disabled?: boolean,
                                  onClick(event?: React.ChangeEvent<HTMLInputElement>) : void}) => {
                                      if(option.header) {
                                          return (<div className={'multiselect-dropdown-header'}>
                                              <span>{option.label}</span>
                                          </div>);
                                      }
                                      return (<div className={`item-renderer ${disabled ? " disabled" : ""}`} /*onClick={() => onClick()}*/>
                                      <input
                                          type="checkbox"
                                          onChange={onClick}
                                          checked={checked}
                                          tabIndex={-1}
                                          disabled={disabled}
                                          hidden
                                      />
                                          <span  style={{paddingLeft: '5px'}}>{option.label}</span>
                                      </div>);
                                  }
                              }
                              maxResult={1000}
                              ClearSelectedIcon={' '}
                          />
                          </Col>
                          <Col md={2} >
                          <ToggleButtonGroup name='Vy' type="radio" value={this.state.graphView} onChange={(val:any) => this.setState({graphView:val})}>
                            <ToggleButton id="tbg-btn-1" value={1}>
                              Graf
                            </ToggleButton>
                            <ToggleButton id="tbg-btn-2" value={0}>
                              Lista
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Col>
                        </Row>
                        }
                        {this.state.isLoading &&
                            <div style={{
                                position: 'absolute',
                                width: '100vw',
                                display: 'flex',
                                alignItems: 'center',
                                verticalAlign: 'middle',
                                left: 0,
                                height: '600px',
                                textAlign: 'center',
                                backgroundColor: '#ffffff00',
                                zIndex: 10
                            }}>
                                <div style={{textAlign: 'center', width: '100%'}}>
                                    <Loading>Laddar...</Loading>
                                </div>
                            </div>
                        }
                        {
                          this.state.graphView == 1 &&
                          <>
                          <Row style={{margin: "1rem 0"}}>
                            <div style={{marginBottom: 10}}>
                                <div style={{ display: 'inline-block'}}>
                                    <span style={{fontSize:'1.25rem', marginRight: '0.5rem'}}>Från:</span> 
                                    <ReactDayPickerInput 
                                        formatDate={(date) => date.toISOString().slice(0, 10)}
                                        value={this.state.startDate}
                                        placeholder={'ÅÅÅÅ-MM-DD'}
                                        inputProps={{ style:{ textAlign:'center', borderRadius: '0.25rem', borderWidth:'1px' } }}
                                        dayPickerProps={{
                                            locale: 'sv',
                                            months: months_long_se,
                                            weekdaysLong: weekdays_long_se,
                                            weekdaysShort: weekdays_short_se
                                        }}
                                        onDayChange={(e) => this.setState({startDate: e})}
                                    />
                                </div>
                                <div style={{ display: 'inline-block', padding: '0 1rem'}}>
                                    <span style={{fontSize:'1.25rem', marginRight: '0.5rem'}}>Till:</span> 
                                    <ReactDayPickerInput 
                                        formatDate={(date) => date.toISOString().slice(0, 10)}
                                        value={this.state.endDate}
                                        placeholder={'ÅÅÅÅ-MM-DD'}
                                        inputProps={{ style:{ textAlign:'center', borderRadius: '0.25rem', borderWidth:'1px' } }}
                                        dayPickerProps={{
                                            locale: 'sv',
                                            months: months_long_se,
                                            weekdaysLong: weekdays_long_se,
                                            weekdaysShort: weekdays_short_se
                                        }}
                                        onDayChange={(e) => this.setState({endDate: e})}
                                    />
                                </div>
                              </div>
                            <Col lg style={{padding:0}}>
                            
                            <Multiselect
                                value={this.state.courseFilter}
                                onChange={(selected: any) => this.setState({courseFilter: selected})}
                                options={this.state.courses.map(courseName => ({value:courseName, label:courseName}))}
                                valueRenderer={() => "Kurser"}
                              />
                            </Col>
                          </Row>
                          <br/>
                          <ResponsiveContainer width="100%" height={600}>
                          <LineChart width={500} height={300} 
                            data={[{time:this.state.startDate.valueOf()},...this.state.graphData, {time:this.state.endDate.valueOf()}]}>
                            <XAxis 
                              padding={{ right: 40, left: 0}} 
                              allowDataOverflow={true}  
                              scale="time" 
                              interval={0} 
                              dataKey="time" 
                              ticks={this.getTicks(this.state.startDate, this.state.endDate, 1)} 
                              tickFormatter={value =>  new Date(value).toLocaleDateString(undefined, {month:'short', year:'2-digit'})} 
                              />
                            <YAxis  padding={{ top: 30, bottom: 30 }} domain={[1,4]} ticks={[1,2,3,4]} tickFormatter={value => this.prognosisOptions[value-1].value}/>
                            <Tooltip content={((value:any) => {
                              return <div style={{background: "#ffffff", padding: 5, border: "solid 1px black"}}>
                                <label>{new Date(value.label).toLocaleDateString()}</label>
                                <table>
                                  <thead>
                                  <tr>
                                    <th style={{padding: "5px 10px"}}>Kurs</th>
                                    <th style={{padding: "5px 10px"}}>Prognos</th>
                                    <th style={{padding: "5px 10px"}}>Betyg</th>
                                    <th style={{padding: "5px 10px"}}>Årskurs</th>
                                  </tr>
                                  </thead>
                                  <tbody>
                                  {
                                    value.payload?.map((load:any) => {
                                      return <tr key={load.name}>
                                      <td style={{padding: "5px 10px", color: load.color}}>{load.name}</td>
                                      <td style={{padding: "5px 10px", color: load.color}}>{this.prognosisOptions[load.value-1].value}</td>
                                      <td style={{padding: "5px 10px", color: load.color}}>{load.payload[`${load.name}grade`]}</td>
                                      <td style={{padding: "5px 10px", color: load.color}}>{load.payload[`${load.name}schoolYear`]}</td>
                                    </tr>
                                    })
                                  }
                                  </tbody>
                                </table>
                              </div>
                              
                              })}/>
                            <Legend />
                            <CartesianGrid strokeDasharray="1 1" />
                            {
                              lines
                            }
                          </LineChart>
                          </ResponsiveContainer>
                        </>
                        }
                        {
                          this.state.graphView == 0 &&
                          <div style={{margin: "10px 0px"}}>
                            <h4>Kurser</h4>
                            {
                              this.state.courses.length == 0 &&
                              <Alert key={1} variant={'light'}>
                                Inga kurser att visa
                              </Alert>
                            }
                          {
                            this.state.courses.map(courseName => {
                            const courseData = this.state.listData
                              .filter(data => data.forecast.course.displayName == courseName)
                              .sort((a, b) => b.time - a.time)
                            const content = (<div className='content'>
                              <Table keyField={'key'} columns={this.columns} data={courseData} loading={this.state.isLoading} searchHidden={true} />
                            </div>)
                            return <Accordion content={content} open={false} title={courseName} />
                            })
                          }
                          </div>
                        }
                        
                </div>
      </div>
  }
}